// Refactored from https://github.com/Azure-Samples/ms-identity-javascript-react-tutorial/blob/main/1-Authentication/1-sign-in/SPA/src/authConfig.js

import { AuthenticationResult, IPublicClientApplication } from "@azure/msal-browser";
// Type-only: api.ts imports this module, so a value import would be circular.
import type { AuthSetup } from "./api/openapi.gen";

// Fetch the auth setup JSON data from the API if not already cached
async function fetchAuthSetup(): Promise<AuthSetup> {
    const response = await fetch("/auth_setup");
    if (!response.ok) {
        throw new Error(`auth setup response was not ok: ${response.status}`);
    }
    return await response.json();
}

const authSetup = await fetchAuthSetup();

export const useLogin = authSetup.useLogin;

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
// The backend only omits the client id when login is disabled, and MSAL is only
// created when it isn't.
export const msalConfig = {
    ...authSetup.msalConfig,
    auth: { ...authSetup.msalConfig.auth, clientId: authSetup.msalConfig.auth.clientId ?? "" }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://learn.microsoft.com/entra/identity-platform/permissions-consent-overview#openid-connect-scopes
 */
export const loginRequest = authSetup.loginRequest;

const tokenRequest = authSetup.tokenRequest;
const pendingTokenRequests = new WeakMap<IPublicClientApplication, Promise<string | undefined>>();

// Build an absolute redirect URI using the current window's location and the relative redirect URI from auth setup
export const getRedirectUri = () => {
    return window.location.origin + authSetup.msalConfig.auth.redirectUri;
};

/**
 * Determines if the user is logged in either via the MSAL public client application or the app services login.
 * @param {IPublicClientApplication | undefined} client - The MSAL public client application instance, or undefined if not available.
 * @returns {Promise<boolean>} A promise that resolves to true if the user is logged in, false otherwise.
 */
export const checkLoggedIn = async (client: IPublicClientApplication | undefined): Promise<boolean> => {
    if (client) {
        const activeAccount = client.getActiveAccount();

        if (activeAccount) {
            return true;
        }
    }

    return false;
};

// Get an access token for use with the API server.
// ID token received when logging in may not be used for this purpose because it has the incorrect audience
// Use the access token from app services login if available
export const getToken = async (client: IPublicClientApplication): Promise<string | undefined> => {
    const pendingRequest = pendingTokenRequests.get(client);
    if (pendingRequest) {
        return pendingRequest;
    }

    let request: Promise<string | undefined>;
    request = client
        .acquireTokenSilent({
            ...tokenRequest,
            redirectUri: getRedirectUri()
        })
        .then(r => {
            return r.accessToken;
        })
        .catch(_ => {
            return client.acquireTokenPopup({ ...tokenRequest, redirectUri: getRedirectUri() });
        })
        .then(r => {
            if (typeof r == "string") {
                return r as string;
            }

            return (<AuthenticationResult>r).accessToken;
        })
        .catch(error => {
            console.log(error);
            return undefined;
        })
        .finally(() => {
            if (pendingTokenRequests.get(client) === request) {
                pendingTokenRequests.delete(client);
            }
        });
    pendingTokenRequests.set(client, request);
    return request;
};

export const isInternalEmployee = async (client: IPublicClientApplication): Promise<boolean> => {
    const userName = await getUsername(client);
    return userName !== null && userName !== undefined && /@arthrex\.\w+/.test(userName);
};

/**
 * Retrieves the username of the active account.
 * If no active account is found, attempts to retrieve the username from the app services login token if available.
 * @param {IPublicClientApplication} client - The MSAL public client application instance.
 * @returns {Promise<string | null>} The username of the active account, or null if no username is found.
 */
export const getUsername = async (client: IPublicClientApplication): Promise<string | null> => {
    const activeAccount = client.getActiveAccount();
    if (activeAccount) {
        return activeAccount.username;
    }

    return null;
};

/**
 * Retrieves the token claims of the active account.
 * If no active account is found, attempts to retrieve the token claims from the app services login token if available.
 * @param {IPublicClientApplication} client - The MSAL public client application instance.
 * @returns {Promise<Record<string, unknown> | undefined>} A promise that resolves to the token claims of the active account, the user claims from the app services login token, or undefined if no claims are found.
 */
export const getTokenClaims = async (client: IPublicClientApplication): Promise<Record<string, unknown> | undefined> => {
    const activeAccount = client.getActiveAccount();
    if (activeAccount) {
        return activeAccount.idTokenClaims;
    }

    return undefined;
};
